<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import Icon from '@/components/partials/Icon.vue'
import { useSessionStore } from "@/stores/useSessionStore"
import { storeToRefs } from "pinia"

type Variant = 'default'

type Size = 'md'

type Props = {
  variant?: Variant
  size?: Size
  withDropdownArrow?: boolean
}

withDefaults(defineProps<Props>(), {
  variant: 'default',
  size: 'md',
})

const emit = defineEmits<{
  (e: 'openModal'): void
}>()

const { t } = useI18n()
const sessionStore = useSessionStore()

const { location } = storeToRefs(sessionStore)

function openAddressModal() {
  emit('openModal')
}
</script>

<template>
  <div class="address-button"
       :class="{
         [`address-button--variant-${variant}`]: variant && variant !== 'default',
         [`address-button--size-${size}`]: true
       }"
       data-test-id="address-button"
       @click="openAddressModal"
  >
    <div class="address-button__content">
      <Icon name="map-pin-line"/>
      <div class="address-button__text">
        <template v-if="location !== null">
          {{ location.street }} {{ location.houseNumber }}
          <span class="address-button__text-zipcode">{{ location.zipcode }} {{ location.city }}</span>
        </template>
        <template v-else>
          {{ t('forms.address.no-address') }}
        </template>
      </div>
      <Icon v-if="withDropdownArrow"
            name="arrow-down-s-line"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/css/mixins/styling.scss";

.address-button {
  $self:    &;
  position: relative;
  cursor:   pointer;
  flex:     1;
  min-width: 0;
  max-width: max-content;

  &__content {
    z-index:          2;
    position:         relative;
    background-color: #ffffff;
    border:           2px solid #e9ecf2;
    border-radius:    9999px;
    display:          flex;
    gap:              0.5rem;
    padding:          0.4rem 1rem;
    transition:       all 0.2s;
  }

  &__text {
    font-weight: 700;
    font-size:   0.875rem;
    height:      1.5rem;
    overflow:    hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__text-zipcode {
    font-weight: 500;
  }

  &:before {
    content:    "";
    z-index:    1;
    position:   absolute;
    top:        -0.4rem;
    left:       -0.4rem;
    border-radius: 9999px;
    width:      calc(100% + 0.8rem);
    height:     calc(100% + 0.8rem);
    @include gradient-button($new-gradient-brand-main-10);
    transform:  scale(0.5);
    transition: all 0.2s;
  }

  &:hover {
    #{$self}__content {
      border-color: var(--color-brand-primary-50);
    }

    &:before {
      transform: scale(1);
    }
  }

  &--size-lg {
    #{$self}__content {
      padding: 0.8rem 1.6rem;
    }

    &:deep(.b-icon-icon),
    &:deep(.b-icon-icon svg) {
      width: 1.25rem;
      height: 1.25rem;
    }
  }

  &--variant-grey {
    #{$self}__content {
      border:           none;
      background-color: #f8f8fa;
      padding:          calc(0.4rem + 2px) calc(1rem + 2px);
    }

    &#{$self}--size-lg #{$self}__content {
      padding: calc(0.8rem + 2px) calc(1.6rem + 2px);
    }

    &:hover {
      #{$self}__content {
        background-color: #e9ecf2;
      }

      &:before {
        transform: scale(0);
      }
    }
  }
}
</style>
